import companyTranslations from '~/modules/company/translations';
import documentTranslations from '~/modules/document/translations';
import driverTranslations from '~/modules/driver/translations';
import fleetTranslations from '~/modules/vehicle/translations';
import shipperProjectTranslations from '~/modules/shipper-projects/translations';
import shipperTranslations from '~/modules/shipper/translations';
import transporterContractsTranslations from '~/modules/contract/translations';
import {
  AgreementType,
  ContractSupplyType,
  PaymentSchemeType,
  PipelineStatus,
} from '~/graphql/__generated__/operations/ltms';
import { ACCOUNT_TYPE, TRANSPORTER_TYPE } from '../transporters/constants';

const DICTIONARIES = {
  en: {
    account_abbr: 'T. Account',
    account_type: 'Account Type',
    account_types: {
      [ACCOUNT_TYPE.COMPANY]: 'Company',
      [ACCOUNT_TYPE.INDIVIDUAL]: 'Individual',
      [ACCOUNT_TYPE.OPERATOR]: 'Operator',
    },
    active_insurance: 'Active Insurance',
    agreement_type: 'Agreement Type',
    area_coverage: 'Area Coverage',
    bank_account_number: 'Bank Account Number',
    bank_account_abbr: 'B. Account',
    bank_holder_abbr: 'B. Account Holder',
    bank_name: 'Bank Name',
    bank_owner_name: 'Bank Owner Name',
    branch: 'Branch',
    cc_notes: 'CC Notes',
    contract: transporterContractsTranslations.en,
    contract_type: 'Contract Type',
    create_new: 'Create New Transporter',
    create_new_contract: 'Create New Contract',
    create_new_driver: 'Create New Driver',
    cluster: 'Cluster',
    capacity_number: 'Capacity Number',
    city: 'City',
    create_new_fleet: 'Create New Fleet',
    cod_fee: 'COD Fee',
    cod_flat: 'COD Flat',
    cod_percentage: 'COD Percentage',
    cod_service: 'COD Service',
    cod_services: 'COD Services',
    cod_type: 'COD Type',
    contact_details: companyTranslations.en.contact_details,
    contact_division: companyTranslations.en.contact_division,
    contact_email_abbr: companyTranslations.en.contact_email_abbr,
    contact_name: companyTranslations.en.contact_name,
    contact_phone_number_abbr: companyTranslations.en.contact_phone_number,
    contact_phone_number: companyTranslations.en.contact_phone_number_abbr,
    contact_position: companyTranslations.en.contact_position,
    contract_period: 'Contract Period',
    coverage: 'Coverage',
    daily_incentive_price: 'Daily Incentive Price',
    daily_rental_price: 'Daily Rental Price',
    default_payment_scheme: 'Default Payment Scheme',
    duplicate_pipeline_success_message: 'Successfully duplicate',
    document: documentTranslations.en,
    documents: 'Documents',
    document_expiry_date_abbr: documentTranslations.en.expiry_date_abbr,
    document_link: 'Document Link',
    document_upload: documentTranslations.en.upload,
    download_x_agreement: 'Download {{x}} Agreement',
    downtime_fee: 'Downtime Fee',
    driver: driverTranslations.en,
    dropoff_route: 'Drop-off Route',
    empty_template: 'Empty Template',
    end_date: 'End Date',
    error_messages: {
      error_update_pipeline_details: 'Error Update Pipeline Details',
      error_update_pipeline_status: 'Error Update Pipeline Status',
      failed_generate_document: 'Failed Generate Document',
      please_fill_required_fields: 'Please Fill Required Fields',
    },
    existing_data_only: 'Existing Data',
    existing_data: 'Existing Transporter Data',
    existing_contract_data: 'Existing Contract Data',
    existing_driver_data: 'Existing Driver Data',
    existing_fleet_data: 'Existing Fleet Data',
    expiration_date: 'Expiration Date',
    fleet: fleetTranslations.en,
    helper: 'Helper',
    helper_name: 'Helper Name',
    helper_contact: 'Helper Contact',
    id: 'Transporter Id',
    initial_abbr: 'T. Initial',
    insurance_start: 'Insurance Start',
    insurance_end: 'Insurance End',
    invalid_input_columns_quantity: 'start_date, end_date, pickup_route, dropoff_route, and rental_price should have the same input quantity',
    modal_duplicate_pipeline_description: 'Pipeline list below will be duplicated into a new pipeline code. You can use this for contract extension purpose.',
    name_abbr: 'T. Name',
    no_of_drop_point: 'No of Drop Point',
    not_yet_signed: 'Not Yet Signed',
    number_of_trip: 'Number of Trip',
    overtime_fee: 'Overtime Fee',
    official_address: 'Official Address',
    payment_details: 'Payment Details',
    payment_scheme: 'Payment Scheme',
    payment_schemes: {
      [PaymentSchemeType.PaymentScheme_100]: '100',
      [PaymentSchemeType.PaymentScheme_25_25_25_25]: '25-25-25-25',
      [PaymentSchemeType.PaymentScheme_50_50]: '50-50',
      [PaymentSchemeType.PaymentScheme_50_50Tip]: '50-50-TIP',
    },
    pic_phone_number: companyTranslations.en.pic_phone_number,
    pipeline: {
      account_name: 'Account Name',
      addendum_link: 'Addendum Link',
      addendum_link_if_any: 'Addendum Link (if any)',
      addendum_or_termination_link: 'Addendum or Termination Link',
      admin_name: 'Admin Name',
      agreement_details: 'Agreement Details',
      agreement_link: 'Agreement Link',
      agreement_link_or_confirmation_screenshots: 'agreement link or confirmation screenshots',
      agreement_number: 'Agreement Number',
      agreement_type: 'Agreement Type',
      agreement_types: {
        [AgreementType.Revertback]: 'Revertback (No TIP)',
        [AgreementType.Tip]: 'TIP',
        [AgreementType.FleetOnlyRental]: 'Fleet Only Rental (Sewa Lepas Kunci)',
      },
      approval_code: 'Approval Code',
      coordinator: 'Coordinator',
      bd_name: 'BD Name',
      close_modal: {
        cancel: 'Yes, Cancel',
        content: 'If you cancel, you will have to review and input the data again.',
        not_cancel: 'No, Go Back',
        title: 'Cancel Reviewing Pipeline',
        convert_to_contract_title: 'Cancel Creating Contract',
      },
      cod_services: 'COD Services',
      convert_to_contract: 'Convert to Contract',
      driver_account_number: 'Driver Account Number',
      driver_bank_account: 'Driver Bank Account',
      driver_details: 'Driver Details',
      dropoff_route: 'Drop-off Route',
      entity: 'Entity',
      extra_helper: 'Extra Helper',
      fleet_details: 'Fleet Details',
      general_information: 'General Information',
      number_of_trip: 'Number of Trip',
      onboarding_status: 'Onboarding Status',
      payment_details: 'Payment Details',
      pic_for_company: 'PIC (For Company)',
      pickup_route: 'Pickup Route',
      pipeline_details: 'Pipeline Details',
      pkp_amount_percentage: 'PKP Amount (%)',
      proof_of_signing: 'Proof of Signing',
      project_alias: 'Project Alias',
      pkp_end_date: 'PKP End Date',
      route_and_price_details: 'Route & Price Details',
      route_details: 'Route Details',
      search_x_not_found: '{{x}} profile not found. Make sure you already create the {{x}} profile first at LTMS',
      select_agreement_document: 'Select type of agreements that you want to download.',
      skb_amount_percentage: 'SKB Amount (%)',
      successfully_converted_to_contract: 'Successfully converted to contract',
      skb_end_date: 'SKB End Date',
      transporter_details: 'Transporter Details',
    },
    pipeline_code: 'Pipeline Code',
    pipeline_lists: 'Pipeline lists',
    pool_city_and_province: 'Pool City & Province',
    pool_district_city_province: 'Pool District City Province',
    pool_full_address: 'Pool Full Address',
    pool_gmaps: 'Pool Gmaps',
    pool_postal_code: 'Pool Postal Code',
    production_date: 'Production Date',
    project: shipperProjectTranslations.en,
    rental_price: 'Rental Price',
    replacement_start_date: 'Replacement Start Date',
    replacement_end_date: 'Replacement End Date',
    scheme_type: 'Scheme Type',
    shipper: shipperTranslations.en,
    shipper_name: 'Shipper Name',
    skb_ownership: 'SKB Ownership',
    start_date: 'Start Date',
    supply_type: 'Supply Type',
    supply_types: {
      [ContractSupplyType.AllIn]: 'All In',
      [ContractSupplyType.DriverOnlyRental]: 'Driver Only Rental',
      [ContractSupplyType.FleetAndDriverRental]: 'Fleet and Driver Rental',
      [ContractSupplyType.FleetOnlyRental]: 'Fleet Only Rental',
    },
    tax_vat: 'Tax VAT',
    tax_wht: 'Tax WHT',
    tip_price: 'TIP Price',
    transporter: 'Transporter',
    transporter_data: 'Transporter Data',
    transporter_name: 'Transporter Name',
    transporter_pipeline: 'Transporter Pipeline',
    transporter_pipeline_status: {
      [PipelineStatus.AgreementCreated]: 'Agreement Created',
      [PipelineStatus.Cancelled]: 'Cancelled',
      [PipelineStatus.Completed]: 'Completed',
      [PipelineStatus.NeedsMoreInfo]: 'Needs More Info',
      [PipelineStatus.NeedsReview]: 'Needs Review',
      [PipelineStatus.SentToLegal]: 'Sent to Legal',
      [PipelineStatus.SentToTransporter]: 'Sent to Transporter',
      [PipelineStatus.TransporterContacted]: 'Transporter Contacted',
      [PipelineStatus.TransporterSigned]: 'Transporter Signed',
      [PipelineStatus.Verified]: 'Verified',
      [PipelineStatus.Voided]: 'Voided',
    },
    transporter_type: 'Transporter Type',
    transporter_types: {
      [TRANSPORTER_TYPE.DRIVER_OPERATOR]: 'Driver Operator',
      [TRANSPORTER_TYPE.INTERNAL_HIRED_DRIVER]: 'Internal Hired Driver',
      [TRANSPORTER_TYPE.MOTORIST]: 'Motorist',
      [TRANSPORTER_TYPE.TRANSPORTER_HIRED_DRIVER]: 'Transporter Hired Driver',
      [TRANSPORTER_TYPE.TRUCK_AND_DRIVER_PLUS_UJ]: 'Truck and Driver Plus UJ',
    },
    traveling_expenses_fee: 'Traveling Expenses Fee',
    unfulfill_fee: 'Unfulfill Fee',
    unfulfilled_fee: 'Unfulfilled Fee',
    update_existing_contract: 'Update Existing Contract',
    update_existing_data: 'Update Existing Transporter',
    update_existing_driver: 'Update Existing Driver',
    update_existing_fleet: 'Update Existing Fleet',
    upload_new_data: 'Upload New Data',
    upload_to_edit_data: 'Upload to Edit Data',
    vcc_notes: 'VCC Notes',
    view_map: 'View Map',
    x_days_before_contract_end: '{{x}} days before Contract End',
    x_days_to_expiration: '{{x}} days to expiration',
  },
};

export default DICTIONARIES;
